import { getLang, katalog, type Katalog, type Lang } from "../i18n/lang";
import type { BadgeStatus } from "./badge";

export interface StatusInfo {
  /** Anzeige-Label in der aktiven UI-Sprache. */
  label: string;
  /** Badge-Farbkategorie. */
  status: BadgeStatus;
}

/** Optionaler Sprach-Überschreiber (für Tests und Dry-Runs). Ohne Angabe gilt
 *  die aktive Oberflächen-Sprache. */
interface LangOpt {
  lang?: Lang;
}

type StatusKey = keyof Katalog["labels"]["status"];
export type StatusDomain = keyof Katalog["labels"]["statusDomain"];

/** Zentrale Status-Map — deckt alle real vorkommenden Status-Strings aus
 *  HeyGen, Blog, Claims, Datasets, Documents, Kanban, Gantt, Presentations ab
 *  (Inventar 2026-07-07). Keys werden case-insensitiv normalisiert.
 *
 *  Hier steht nur noch die FARBE: sie gehört zum Zustand und nicht zur Sprache.
 *  Der Text kommt aus `i18n/<lang>/labels.ts`. Der Schlüssel-Typ ist an den
 *  Katalog gebunden — ein Zustand ohne Text (oder ein Text ohne Zustand) ist ein
 *  Compiler-Fehler, kein stiller Rückfall auf den rohen Enum-Wert. */
const STATUS_BADGE: Record<StatusKey, BadgeStatus> = {
  // ---- Task / Kanban / Gantt ----
  todo: "neutral",
  open: "neutral",
  new: "info",
  in_progress: "run",
  ongoing: "run",
  "on hold": "warning",
  on_hold: "warning",
  feedback: "info",
  done: "success",
  closed: "success",
  lost: "danger",

  // ---- Claims ----
  pending: "warning",
  approved: "success",
  rejected: "danger",

  // ---- Datasets ----
  draft: "neutral",
  queued: "info",
  extracting: "run",
  ready: "success",
  error: "danger",
  failed: "danger",

  // ---- Documents (Embedding + Summary) ----
  doc_ai_pending: "warning",
  processing: "run",

  // ---- Blog-Pipeline ----
  researching: "run",
  toc_building: "run",
  toc_approved: "info",
  skeleton_building: "run",
  generating: "run",
  generated: "info",
  compliance_blocked: "danger",
  reviewer_blocked: "warning",
  needs_human_review: "warning",
  publishing: "run",
  published: "success",
  archived: "neutral",

  // ---- HeyGen (Script + Video) ----
  // ---- Presentations ----
  planning: "run",
  planned: "info",
  rendered: "success",

  claim_picking: "run",
  scene_building: "run",
  exported: "success",
  completed: "success",

  // ---- Generische Zustände ----
  running: "run",
  active: "run",
  success: "success",
  ok: "success",
  cancelled: "neutral",
  canceled: "neutral",
};

/** Kontext-spezifische Overrides. Für Dokumente heißt der Abschluss-Zustand
 *  „Verarbeitet" (nicht „Erledigt" wie bei Aufgaben) — dasselbe rohe „done"
 *  wird domänenabhängig übersetzt, ohne das globale Mapping zu verbiegen. */
const DOMAIN_BADGE: {
  [D in StatusDomain]: Record<keyof Katalog["labels"]["statusDomain"][D], BadgeStatus>;
} = {
  document: {
    done: "success",
    completed: "success",
    pending: "warning",
  },
};

/** Übersetzt einen rohen Status-String in Label + Badge-Kategorie.
 *  Unbekannte Werte kommen unverändert als „neutral" zurück.
 *  Mit `opts.domain` greifen kontext-spezifische Overrides (z. B. Dokumente).
 *
 *  MEHRSPRACHIGKEIT (docs/i18n.md): der TEXT kommt aus dem Katalog der aktiven
 *  Sprache (`i18n/<lang>/labels.ts`), die FARBE immer aus den Maps hier.
 *  `opts.lang` überschreibt die aktive Sprache. */
export function statusLabel(
  raw: string | null | undefined,
  opts?: LangOpt & { domain?: StatusDomain }
): StatusInfo {
  if (!raw) return { label: "—", status: "neutral" };
  const key = raw.trim().toLowerCase();
  const texte = katalog(opts?.lang ?? getLang()).labels;
  if (opts?.domain) {
    const badge = (DOMAIN_BADGE[opts.domain] as Record<string, BadgeStatus>)[key];
    if (badge) {
      const text = (texte.statusDomain[opts.domain] as Record<string, string>)[key];
      return { label: text, status: badge };
    }
  }
  const badge = (STATUS_BADGE as Record<string, BadgeStatus>)[key];
  if (badge) return { label: (texte.status as Record<string, string>)[key], status: badge };
  return { label: raw, status: "neutral" };
}

/** Analyse-Stufen (nie Modell-IDs in der UI zeigen, Spec §0).
 *  Deutsch festgeschrieben — wer die Stufen in der aktiven Sprache braucht,
 *  nimmt `levelLabel()`. */
export const LEVEL_LABELS: Record<string, string> = katalog("de").labels.level;

export function levelLabel(raw: string | null | undefined, opts?: LangOpt): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const texte = katalog(opts?.lang ?? getLang()).labels.level as Record<string, string>;
  return texte[key] ?? raw;
}

/** Title-Case-Fallback für unbekannte rohe Enum-Werte: `study_x` → „Study X".
 *  So bleibt die UI auch bei künftigen Server-Enums lesbar (kein roher Key). */
function titleCaseFallback(raw: string): string {
  return raw
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Library-Klassifikation + Figur-Typen (Migrationen 0064/0065). Der Server
 *  liefert die Werte teils GROSS (z. B. „DECORATIVE"/„STUDY_GRAPHIC") — daher
 *  case-insensitiver Key. Deckt sowohl `classification` als auch `figure_type`
 *  ab (eine gemeinsame Map, keine Enum→Label-Logik duplizieren). Unbekannt →
 *  Title-Case-Fallback statt rohem Enum. */
export function classificationLabel(
  raw: string | null | undefined,
  opts?: LangOpt
): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const texte = katalog(opts?.lang ?? getLang()).labels.bildKlassifikation as Record<
    string,
    string
  >;
  return texte[key] ?? titleCaseFallback(raw);
}

/** Rollen von Blog-Bild-Evidence-Blöcken (app/services/blog_publish.py).
 *  Roh z. B. „IMAGE_HERO" — case-insensitiv, unbekannt → Title-Case-Fallback. */
export function imageRoleLabel(raw: string | null | undefined, opts?: LangOpt): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const texte = katalog(opts?.lang ?? getLang()).labels.bildRolle as Record<string, string>;
  return texte[key] ?? titleCaseFallback(raw);
}

/** Name des system-seedeten Projekts in der DATENBANK. Er ist der Lookup-Key
 *  des per-User-SOP-Scopings (sync_users.py) und wird NIE umbenannt — nur die
 *  Anzeige wird ersetzt. USER-Projektnamen bleiben unverändert. */
const SOP_PROJEKT_DB_NAME = "My SOPs";

export function projectDisplayName(
  name: string | null | undefined,
  opts?: LangOpt
): string {
  if (!name) return name ?? "";
  if (name !== SOP_PROJEKT_DB_NAME) return name;
  return katalog(opts?.lang ?? getLang()).labels.projektMeineSops;
}
